import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import swal from 'sweetalert';
import LoadingBar from 'react-top-loading-bar';
import { useIsOnline } from 'react-use-is-online';
import { useCreateCategoryMutation } from '../../Features/categories';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../Features/auth/authSlice';

import {
    ArrowLeftIcon,
    TagIcon,
    SaveIcon,
    XIcon,
} from '@heroicons/react/outline';

function CreateCategoryView() {
    const navigate = useNavigate();
    const [progress, setProgress] = useState(0);
    const { isOnline } = useIsOnline();
    const user = useSelector(selectCurrentUser);

    const [createCategory, { isLoading }] = useCreateCategoryMutation();

    const [input, setInput] = useState({
        name: '',
        user_id: user.id,
    });

    const [errors, setErrors] = useState({
        name: '',
    });

    const handleChange = (e) => {
        const { name, value } = e.target;

        setInput((prev) => ({
            ...prev,
            [name]: value,
        }));

        setErrors((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isOnline) {
            return swal(
                'No Internet connection',
                'Please check Internet connection and try again'
            );
        }

        setErrors({
            name: '',
        });

        try {
            setProgress(80);

            const resp = await createCategory(input).unwrap();

            if (resp?.hasOwnProperty('success')) {
                setProgress(100);
                return navigate(-1);
            }
        } catch (err) {
            setProgress(100);

            if (err?.data?.hasOwnProperty('errors')) {
                setErrors({
                    name: err.data.errors?.name || '',
                });
            } else if (err?.data?.hasOwnProperty('error')) {
                setInput((prev) => ({
                    ...prev,
                    errorMassage: err.data.error,
                }));
            } else {
                swal(
                    'Error',
                    err?.data?.message ||
                        err?.message ||
                        'Something went wrong while creating the category.',
                    'error'
                );
            }
        }
    };

    const back = () => {
        if (!isOnline) {
            return swal(
                'No Internet connection',
                'Please check Internet connection and try again'
            );
        }

        setProgress(100);
        navigate(-1);
    };

    return (
        <div className="w-full min-h-screen bg-gray-100 dark:bg-gray-950 pb-12">
            <LoadingBar
                color="#80b643"
                height="4px"
                progress={progress}
                onLoaderFinished={() => setProgress(0)}
            />

            {/* Header */}
            <div className="w-full px-4 sm:px-6 lg:px-8 pt-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                            <TagIcon className="w-6 h-6 text-[#80b643]" />
                        </div>

                        <div>
                            <h1 className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-white">
                                Create Category
                            </h1>

                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Add a new category to your system
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={back}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-200 hover:border-[#80b643] hover:text-[#80b643] transition"
                    >
                        <ArrowLeftIcon className="w-4 h-4" />
                        Back
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="w-full px-4 sm:px-6 lg:px-8 mt-6">
                <div className="max-w-4xl mx-auto">
                    <form onSubmit={handleSubmit} autoComplete="off">
                        {/* Category Information */}
                        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">
                            {/* Card Header */}
                            <div className="px-5 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-800">
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                                        <TagIcon className="w-5 h-5 text-[#80b643]" />
                                    </div>

                                    <div>
                                        <h2 className="text-sm font-semibold text-gray-800 dark:text-white">
                                            Category Information
                                        </h2>

                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            Enter the details for the new
                                            category
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Form */}
                            <div className="p-5 sm:p-6">
                                <div>
                                    <label
                                        htmlFor="name"
                                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                    >
                                        Category Name
                                    </label>

                                    <input
                                        type="text"
                                        name="name"
                                        id="name"
                                        value={input.name}
                                        onChange={handleChange}
                                        placeholder="Enter category name"
                                        autoFocus
                                        className={`w-full px-4 py-3 rounded-lg border ${
                                            errors.name
                                                ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                                                : 'border-gray-200 dark:border-gray-700 focus:border-[#80b643] focus:ring-[#80b643]/20'
                                        } bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-white placeholder-gray-400 outline-none focus:ring-2 transition`}
                                    />

                                    {errors.name && (
                                        <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                                            {errors.name}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="mt-5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm p-4 sm:p-5">
                            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
                                <button
                                    type="button"
                                    onClick={back}
                                    disabled={isLoading}
                                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <XIcon className="w-4 h-4" />
                                    Cancel
                                </button>

                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-[#80b643] hover:bg-[#719f3a] text-white text-sm font-medium border border-[#80b643] shadow-sm transition disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    <SaveIcon className="w-4 h-4" />

                                    {isLoading
                                        ? 'Creating...'
                                        : 'Create Category'}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default CreateCategoryView;